import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Navigation } from 'lucide-react';
import { DAY_COLORS } from './plannerHelpers';
import useRoutes from './useRoutes';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const TILE_URL = MAPBOX_TOKEN
  ? `https://api.mapbox.com/styles/v1/mapbox/light-v11/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`
  : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTR = MAPBOX_TOKEN ? '&copy; Mapbox' : '&copy; OpenStreetMap contributors';

function FitBoundsController({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length > 1) {
      map.fitBounds(L.latLngBounds(positions), { padding: [40, 40], maxZoom: 13 });
    } else if (positions.length === 1) {
      map.setView(positions[0], 13);
    }
  }, [positions, map]);
  return null;
}

function MapFlyController({ activities, selectedActivityId }) {
  const map = useMap();
  useEffect(() => {
    if (!selectedActivityId) return;
    const a = activities.find(act => act.id === selectedActivityId);
    if (a?.latitude && a?.longitude) {
      map.flyTo([a.latitude, a.longitude], Math.max(map.getZoom(), 14), { duration: 0.8 });
    }
  }, [selectedActivityId, activities, map]);
  return null;
}

function makeMarkerIcon(color, num, isFirst, isSelected) {
  const size = isSelected ? 32 : isFirst ? 28 : 22;
  const bg = isSelected ? 'white' : color;
  const border = isSelected ? `3px solid ${color}` : '2.5px solid white';
  const textColor = isSelected ? color : 'white';
  const shadow = isSelected ? '0 2px 10px rgba(0,0,0,0.35)' : '0 2px 5px rgba(0,0,0,0.2)';
  return L.divIcon({
    className: '',
    html: `<div style="
      width:${size}px;height:${size}px;
      background:${bg};
      border:${border};
      border-radius:50%;
      display:flex;align-items:center;justify-content:center;
      color:${textColor};
      font-size:${isFirst || isSelected ? 11 : 10}px;
      font-weight:800;
      box-shadow:${shadow};
      font-family:system-ui,sans-serif;
      cursor:pointer;
    ">${num}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

export default function MapTabView({ activities, days, itinerary, compact = false, selectedActivityId, onActivitySelect }) {
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayLocationLabels, setDayLocationLabels] = useState({});

  const activitiesWithCoords = useMemo(
    () => activities.filter(a => a.latitude && a.longitude && (Math.abs(a.latitude) > 0.1 || Math.abs(a.longitude) > 0.1)),
    [activities]
  );

  useEffect(() => {
    const labels = {};
    activities.forEach(a => {
      if (a.city_name && !labels[a.day_number]) labels[a.day_number] = a.city_name;
    });
    setDayLocationLabels(labels);
  }, [activities]);

  const filteredActivities = useMemo(
    () => selectedDay ? activitiesWithCoords.filter(a => a.day_number === selectedDay) : activitiesWithCoords,
    [activitiesWithCoords, selectedDay]
  );

  const routes = useRoutes(filteredActivities);

  const fitPositions = useMemo(
    () => filteredActivities.map(a => [a.latitude, a.longitude]),
    [filteredActivities]
  );

  const activityMarkers = useMemo(() => {
    const counters = {};
    return filteredActivities
      .sort((a, b) => a.day_number !== b.day_number ? a.day_number - b.day_number : (a.position || 0) - (b.position || 0))
      .map(a => {
        counters[a.day_number] = (counters[a.day_number] || 0) + 1;
        return { ...a, positionInDay: counters[a.day_number] };
      })
      // In "All days" view, only show the first activity per day to avoid number confusion
      .filter(a => selectedDay !== null || a.positionInDay === 1);
  }, [filteredActivities, selectedDay]);

  const handleDayFilter = useCallback((day) => {
    setSelectedDay(prev => prev === day ? null : day);
  }, []);

  const openGoogleMaps = useCallback(() => {
    const pts = filteredActivities.filter(a => a.latitude && a.longitude);
    if (pts.length === 0) return;
    const waypoints = pts.map(a => `${a.latitude},${a.longitude}`).join('/');
    window.open(`https://www.google.com/maps/dir/${waypoints}`, '_blank');
  }, [filteredActivities]);

  if (activitiesWithCoords.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center p-4">
          <p className="text-charcoal-500 font-semibold mb-1 text-sm">No map data yet</p>
          {!compact && <p className="text-platinum-500 text-sm">Generate your itinerary to see activities on the map.</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 relative">
        <MapContainer
          center={fitPositions[0] || [0, 0]}
          zoom={10}
          style={{ height: '100%', width: '100%' }}
          zoomControl={true}
          scrollWheelZoom={true}
        >
          <TileLayer url={TILE_URL} attribution={TILE_ATTR} tileSize={512} zoomOffset={-1} />
          {fitPositions.length > 0 && <FitBoundsController positions={fitPositions} />}
          {selectedActivityId && (
            <MapFlyController activities={activitiesWithCoords} selectedActivityId={selectedActivityId} />
          )}

          {(selectedDay ? [selectedDay] : days).map(day => {
            const color = DAY_COLORS[(day - 1) % DAY_COLORS.length];
            const routeCoords = routes[day];
            if (!routeCoords) return null;
            return (
              <Polyline key={`route-${day}`} positions={routeCoords} pathOptions={{ color, weight: 4, opacity: 0.85 }} />
            );
          })}

          {!selectedDay && Object.entries(routes)
            .filter(([key]) => key.startsWith('between-'))
            .map(([key, coords]) => (
              <Polyline key={key} positions={coords} pathOptions={{ color: '#6B7280', weight: 2.5, opacity: 0.7, dashArray: '5,8' }} />
            ))}

          {activityMarkers.map((activity, idx) => {
            const color = DAY_COLORS[(activity.day_number - 1) % DAY_COLORS.length];
            const isFirst = activity.positionInDay === 1;
            const isSelected = selectedActivityId === activity.id;
            // In "All days" view show day number so it's clear which day each pin belongs to
            const label = selectedDay ? activity.positionInDay : activity.day_number;
            const icon = makeMarkerIcon(color, label, isFirst, isSelected);
            return (
              <Marker
                key={`marker-${activity.id || idx}`}
                position={[activity.latitude, activity.longitude]}
                icon={icon}
                zIndexOffset={isSelected ? 1000 : 0}
                eventHandlers={{
                  click: () => onActivitySelect && onActivitySelect(isSelected ? null : activity.id),
                }}
              >
                <Tooltip direction="top" offset={[0, -10]}>
                  <div className="text-xs font-semibold">
                    <span className="text-platinum-400 mr-1">Day {activity.day_number} ·</span>
                    {activity.title}
                    {activity.location && <div className="text-platinum-400 font-normal">{activity.location}</div>}
                  </div>
                </Tooltip>
              </Marker>
            );
          })}
        </MapContainer>

        {!compact && (
          <div className="absolute top-3 right-3 z-[400]">
            <button
              onClick={openGoogleMaps}
              className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg px-3 py-2 text-xs font-medium text-charcoal-500 hover:bg-white transition-colors flex items-center gap-1.5 border border-platinum-100"
            >
              <Navigation size={13} />
              Google Maps
            </button>
          </div>
        )}
      </div>

      {/* Day filter strip */}
      <div className="bg-white border-t border-platinum-200 px-4 py-2.5 flex items-center gap-2 overflow-x-auto flex-shrink-0">
        <button
          onClick={() => setSelectedDay(null)}
          className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
            !selectedDay ? 'bg-charcoal-500 text-white' : 'bg-platinum-100 text-charcoal-400 hover:bg-platinum-200'
          }`}
        >
          All days
        </button>
        {days.map(day => {
          const color = DAY_COLORS[(day - 1) % DAY_COLORS.length];
          const isActive = selectedDay === day;
          const label = dayLocationLabels[day];
          if (!activitiesWithCoords.some(a => a.day_number === day)) return null;
          return (
            <button
              key={day}
              onClick={() => handleDayFilter(day)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                isActive ? 'text-white shadow-sm' : 'bg-platinum-100 text-charcoal-400 hover:bg-platinum-200'
              }`}
              style={isActive ? { backgroundColor: color } : {}}
            >
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: isActive ? 'rgba(255,255,255,0.7)' : color }} />
              Day {day}{label ? ` · ${label}` : ''}
            </button>
          );
        })}
      </div>
    </div>
  );
}
